import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import { useGetForcaVenda, useGetErroPesquisa, useGetErroPesquisaVendedor } from "~hooks/useSincronizacao";
import { forcadeVendasMock } from "../mocks/new/ForcadeVendas.mock";
import { ErroPesquisaMock } from "../mocks/new/ErroPesquisa.mock";
import { ErroPesquisaVendedor } from "../mocks/new/ErroPesquisaVendedor.mock";
import { extractUpdatedAt } from "../utils/extractUpdatedAt";
import useVendedor from "~fetchs/useVendedor";
import { usarMock } from "../utils/dataSource";

const mockForca         = forcadeVendasMock?.[0]?.[0]?.data?.[0] ?? [];
const mockErro          = ErroPesquisaMock?.[0]?.[0]?.data?.[0] ?? [];
const mockErroVendedor  = ErroPesquisaVendedor?.[0]?.[0]?.data?.[0] ?? [];

const avgNonNull = (values) => {
  const valid = values.filter((v) => v != null);
  if (!valid.length) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
};

const extractItems = (response) => {
  const arr =
    response?.data?.data?.[0] ??
    response?.data?.[0] ??
    response?.[0] ??
    null;
  return Array.isArray(arr) ? arr : null;
};

const flattenVendedorForca = (rawVendedores, vendedorSelecionado) => {
  if (!Array.isArray(rawVendedores) || !rawVendedores.length) return rawVendedores ?? [];

  const isVendedorGrouped = rawVendedores.some((v) => Array.isArray(v?.basesForcaVenda));
  if (!isVendedorGrouped) return rawVendedores;

  const filtered = vendedorSelecionado !== null
    ? rawVendedores.filter((v) => v?.id === vendedorSelecionado)
    : rawVendedores;

  const allItems = filtered.flatMap((v) =>
    (v?.basesForcaVenda ?? []).filter((item) => item?.data !== undefined || item?.cards !== undefined),
  );

  if (vendedorSelecionado !== null) return allItems;


  const visitsByDate = new Map();
  const allCards = [];

  for (const item of allItems) {
    if (item.cards !== undefined) {
      allCards.push(item.cards);
    } else if (item.data !== undefined) {
      const key = item.data.substring(0, 10);
      if (!visitsByDate.has(key)) visitsByDate.set(key, { ...item, totalVisitasValidas: 0 });
      visitsByDate.get(key).totalVisitasValidas += item.totalVisitasValidas ?? 0;
    }
  }

  if (!allCards.length) return Array.from(visitsByDate.values());

  const mergedCard = {
    id: 0,
    totalClientes:         allCards.reduce((a, c) => a + (c?.totalClientes ?? 0), 0),
    visitasPresenciais:    avgNonNull(allCards.map((c) => c?.visitasPresenciais)),
    visitasRemotas:        avgNonNull(allCards.map((c) => c?.visitasRemotas)),
    aderenciaRota:         avgNonNull(allCards.map((c) => c?.aderenciaRota)),
    m300AderenciaRota:     avgNonNull(allCards.map((c) => c?.m300AderenciaRota)),
    coberturaCarteira:     avgNonNull(allCards.map((c) => c?.coberturaCarteira)),
    m300CoberturaCarteira: avgNonNull(allCards.map((c) => c?.m300CoberturaCarteira)),
  };

  return [
    ...Array.from(visitsByDate.values()),
    { id: 1, cards: mergedCard },
  ];
};

const flattenVendedorErroPesquisa = (rawVendedores, vendedorSelecionado) => {
  if (!Array.isArray(rawVendedores) || !rawVendedores.length) return rawVendedores ?? [];

  const isVendedorGrouped = rawVendedores.some((v) => Array.isArray(v?.basesErroPesquisa));
  if (!isVendedorGrouped) return rawVendedores;

  const filtered = vendedorSelecionado !== null
    ? rawVendedores.filter((v) => v?.id === vendedorSelecionado)
    : rawVendedores;

  return filtered.flatMap((v) => v?.basesErroPesquisa ?? []);
};

const aggregateErrosPorTipo = (rawVendedores, vendedorSelecionado) => {
  if (!Array.isArray(rawVendedores) || !rawVendedores.length) return [];

  const filtered = vendedorSelecionado !== null
    ? rawVendedores.filter((v) => v?.id === vendedorSelecionado)
    : rawVendedores;

  const totais = new Map();
  for (const v of filtered) {
    for (const item of v?.basesErroPesquisaVendedor ?? []) {
      const tipo = item?.tipoErro;
      if (!tipo) continue;
      totais.set(tipo, (totais.get(tipo) ?? 0) + (item?.qtdErros ?? 0));
    }
  }

  return Array.from(totais, ([tipoErro, qtdErros]) => ({ tipoErro, qtdErros }));
};


const saveToDexie = async (table, payload) => {
  try {
    await table.clear();
    await table.add({ payload, syncedAt: new Date().toISOString() });
  } catch (err) {
    console.error(`Erro ao salvar ${table.name} no Dexie:`, err);
  }
};



const useSalesForceData = () => {
  const [sfItems, setSfItems]                   = useState(null);
  const [pesquisas, setPesquisas]               = useState(null);
  const [pesquisasVendedor, setPesquisasVendedor] = useState(null);
  const [isRefreshing, setIsRefreshing]         = useState(false);
  const [isLoading, setIsLoading]               = useState(true);

  const { vendedorSelecionado } = useVendedor();

  const hasMock = usarMock(forcadeVendasMock);
  const useApi  = !hasMock && navigator.onLine;

  const { data: forcaData, isFetching: fetchingForca, isRefetching: refetchingForca, isError: errorForca } =
    useGetForcaVenda(useApi, 0, 0, null);

  const { data: erroData, isFetching: fetchingErro, isRefetching: refetchingErro, isError: errorErro } =
    useGetErroPesquisa(useApi, 0, 0, null);

  const { data: erroVendedorData, isFetching: fetchingErroVendedor, isRefetching: refetchingErroVendedor, isError: errorErroVendedor } =
    useGetErroPesquisaVendedor(useApi, 0, 0, null);

  const apiForca         = useMemo(() => hasMock ? mockForca        : extractItems(forcaData),        [hasMock, forcaData]);
  const apiErro          = useMemo(() => hasMock ? mockErro         : extractItems(erroData),         [hasMock, erroData]);
  const apiErroVendedor  = useMemo(() => hasMock ? mockErroVendedor : extractItems(erroVendedorData), [hasMock, erroVendedorData]);

  const loadFromDexie = useCallback(async () => {
    try {
      const [forcaRows, erroRows, erroVendedorRows] = await Promise.all([
        db.forcaVenda.toArray(),
        db.erroPesquisa.toArray(),
        db.erroPesquisaVendedor.toArray(),
      ]);
      if (forcaRows.length)         setSfItems(forcaRows[0].payload ?? null);
      if (erroRows.length)          setPesquisas(erroRows[0].payload ?? null);
      if (erroVendedorRows.length)  setPesquisasVendedor(erroVendedorRows[0].payload ?? null);
    } catch (err) {
      console.error("Erro ao carregar salesForce do Dexie:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    if (hasMock) {
      setSfItems(mockForca);
      setPesquisas(mockErro);
      setPesquisasVendedor(mockErroVendedor);
      setIsLoading(false);
      return;
    }
    loadFromDexie();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    const fetching =
      fetchingForca || refetchingForca ||
      fetchingErro || refetchingErro ||
      fetchingErroVendedor || refetchingErroVendedor;
    setIsRefreshing(fetching);
    if (fetching) return;

    if (!errorForca && apiForca != null) {
      setSfItems(apiForca);
      saveToDexie(db.forcaVenda, apiForca);
    }

    if (!errorErro && apiErro != null) {
      setPesquisas(apiErro);
      saveToDexie(db.erroPesquisa, apiErro);
    }

    if (!errorErroVendedor && apiErroVendedor != null) {
      setPesquisasVendedor(apiErroVendedor);
      saveToDexie(db.erroPesquisaVendedor, apiErroVendedor);
    }
  }, [apiForca, apiErro, apiErroVendedor, hasMock, errorForca, errorErro, errorErroVendedor, fetchingForca, refetchingForca, fetchingErro, refetchingErro, fetchingErroVendedor, refetchingErroVendedor]);


  useEffect(() => {
    const handleOffline = () => {
      if (!hasMock) {
        setIsRefreshing(false);
        loadFromDexie();
      }
    };
    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, [hasMock, loadFromDexie]);

  const normalizedSfItems = useMemo(
    () => flattenVendedorForca(sfItems ?? [], vendedorSelecionado),
    [sfItems, vendedorSelecionado],
  );

  const normalizedPesquisas = useMemo(
    () => flattenVendedorErroPesquisa(pesquisas ?? [], vendedorSelecionado),
    [pesquisas, vendedorSelecionado],
  );

  const errosPorTipo = useMemo(
    () => aggregateErrosPorTipo(pesquisasVendedor ?? [], vendedorSelecionado),
    [pesquisasVendedor, vendedorSelecionado],
  );

  const updatedAt = useMemo(() => extractUpdatedAt(sfItems), [sfItems]);

  return {
    sfItems: normalizedSfItems,
    pesquisas: normalizedPesquisas,
    errosPorTipo,
    updatedAt,
    isLoading,
    isRefreshing,
  };
};

export default useSalesForceData;
